import waiverStatePlans2020 from "../data/2020/waiverStates";
import nonWaiverStatePlans2020 from "../data/2020/nonWaiverStates";
import waiverStatePlans2021 from "../data/2021/waiverStates";
import nonWaiverStatePlans2021 from "../data/2021/nonWaiverStates";
import { plans as waiverStatePlans2022 } from "../data/2022/waiverStates";
import { plans as nonWaiverStatePlans2022 } from "../data/2022/nonWaiverStates";
import { LDFlagSet } from "launchdarkly-js-client-sdk";
import { CONSTANTS, IntlContext } from "../../../helpers";
import { useContext } from "react";
import { useFlags } from "launchdarkly-react-client-sdk";

interface MedigapPlans {
  waiver: { [key: string]: MedigapPlan[] };
  nonWaiver: MedigapPlan[];
}

const medigapPlans: { [key: string]: MedigapPlans } = {
  "2020": { waiver: waiverStatePlans2020, nonWaiver: nonWaiverStatePlans2020 },
  "2021": { waiver: waiverStatePlans2021, nonWaiver: nonWaiverStatePlans2021 },
  "2022": { waiver: waiverStatePlans2022, nonWaiver: nonWaiverStatePlans2022 },
};

export interface GetMedigapPricingYearFlags extends LDFlagSet {
  feEnableNextYearMedigapPricing: boolean;
  mctCurrentPlanYear: string;
}

export const getMedigapPricingYear = ({
  feEnableNextYearMedigapPricing,
  mctCurrentPlanYear,
}: GetMedigapPricingYearFlags): string => {
  // Start with the Medigap pricing year set to the year before the current plan year
  let pricingYear = String(Number(mctCurrentPlanYear) - 1);
  // The `feEnableNextYearMedigapPricing` flag should be enabled most of the year,
  // in which case, the pricing year is set to be the same as the current plan year.
  //
  // Between the end of OE -- when current plan year is set to the next calendar
  // year -- and whenever CMS would like to turn on next year's Medigap pricing,
  // the flag should be turned off (set to `false`)
  if (feEnableNextYearMedigapPricing) {
    pricingYear = mctCurrentPlanYear;
  }
  return pricingYear;
};

const getMedigapPlans = (currentYear: string) =>
  medigapPlans[currentYear] || {
    waiver: {},
    nonWaiver: [],
  };

export interface MedigapPlanPremiumRange {
  plan_type: string;
  medigap_plan_type: MedigapPlanType;
  monthly_rate_min: number;
  monthly_rate_max: number;
}

export interface MedigapPlan {
  state?: string;
  plan_type: string;
  plan_name: string;
  plan_details_name: string;
  plan_policy_name: string;
  part_a_deductible: string;
  part_a_deductible_note?: string;
  part_b_deductible: string;
  part_b_deductible_note?: string;
  part_b_coinsurance: string;
  part_b_coinsurance_note?: string;
  hospice: string;
  plan_benefits: string;
  hospital_stay_1_60: string;
  hospital_stay_61_90: string;
  hospital_stay_91_150: string;
  hospital_stay_after_150?: string;
  hospital_stay_additional_365?: string;
  hospital_stay_after_365?: string;
  snf_days_1_20: string;
  snf_days_21_100?: string;
  snf_days_21_120?: string;
  snf_after_120_days?: string;
  snf_after_100_days?: string;
  snf_days_101_365?: string;
  snf_days_after_365?: string;
  snf_not_covered_1_365?: string;
  snf_not_covered_after_365_days?: string;
  snf_not_days_1_30?: string;
  snf_not_after_30?: string;
  a_blood_first_3: string;
  a_blood_after_3: string;
  ip_mental_health_days_1_60?: string;
  ip_mental_health_days_61_90?: string;
  ip_mental_health_days_91_150?: string;
  ip_mental_health_days_add_175?: string;
  ip_mental_health_days_after_175?: string;
  ip_mental_bio_days_1_60?: string;
  ip_mental_bio_days_61_90?: string;
  ip_mental_bio_days_91_190?: string;
  ip_mental_bio_add_365_days?: string;
  ip_mental_bio_beyond_add_365_days?: string;
  ip_mental_other_first_60?: string;
  ip_mental_other_61?: string;
  ip_mental_other_after_60_cy?: string;
  home_health: string;
  home_health_add_40?: string;
  home_health_after_40?: string;
  home_health_add_365?: string;
  home_health_after_365?: string;
  dme_first: string;
  dme_remainder_then: string;
  covered_medical_services_first: string;
  covered_medical_services_then: string;
  preventive_services?: string;
  prev_serv_cover?: string;
  prev_serv_non?: string;
  phys_ther?: string;
  op_mental_health?: string;
  state_mand?: string;
  excess_charges: string;
  b_blood_first_3: string;
  b_blood_after_3: string;
  tests_diagnostics?: string;
  outpatient_biological_mental_covered_first?: string;
  outpatient_biological_mental_covered_then?: string;
  outpatient_biological_mental_not_covered?: string;
  outpatient_other_mental_covered_first?: string;
  outpatient_other_mental_covered_then?: string;
  outpatient_other_mental_not_covered_1_24_visits?: string;
  outpatient_other_mental_not_covered_25_plus_visits?: string;
  state_mandate_covered_first?: string;
  state_mandate_covered_then?: string;
  state_mandate_not_covered?: string;
  foreign_travel_emergency?: string;
  foreign_travel_emergency_first?: string;
  foreign_travel_emergency_remainder_then?: string;
  out_pocket_limit?: string;
  total_deductible?: string;
}

export interface MedigapPlanInfo {
  plan: MedigapPlan;
  premiumRange: MedigapPlanPremiumRange;
}

export enum MedigapRateType {
  MEDIGAP_RATE_TYPE_ATTAINED_AGE = "MEDIGAP_RATE_TYPE_ATTAINED_AGE",
  MEDIGAP_RATE_TYPE_ISSUE_AGE = "MEDIGAP_RATE_TYPE_ISSUE_AGE",
  MEDIGAP_RATE_TYPE_COMMUNITY_RATED = "MEDIGAP_RATE_TYPE_COMMUNITY_RATED",
}

export interface MedigapPlanPolicy {
  address: string;
  company: string;
  monthly_rate_max: number;
  monthly_rate_min: number;
  phone_number: string;
  rate_type: MedigapRateType;
  website: string;
}

export enum MedigapPlanType {
  A = "MEDIGAP_PLAN_TYPE_A",
  B = "MEDIGAP_PLAN_TYPE_B",
  C = "MEDIGAP_PLAN_TYPE_C",
  D = "MEDIGAP_PLAN_TYPE_D",
  F = "MEDIGAP_PLAN_TYPE_F",
  HIGH_F = "MEDIGAP_PLAN_TYPE_HIGH_F",
  G = "MEDIGAP_PLAN_TYPE_G",
  HIGH_G = "MEDIGAP_PLAN_TYPE_HIGH_G",
  K = "MEDIGAP_PLAN_TYPE_K",
  L = "MEDIGAP_PLAN_TYPE_L",
  M = "MEDIGAP_PLAN_TYPE_M",
  N = "MEDIGAP_PLAN_TYPE_N",
  MA_CORE = "MEDIGAP_PLAN_TYPE_MA_CORE",
  MA_SUPP_1 = "MEDIGAP_PLAN_TYPE_MA_SUPP_1",
  MA_SUPP_1A = "MEDIGAP_PLAN_TYPE_MA_SUPP_1A",
  MN_BASIC = "MEDIGAP_PLAN_TYPE_MN_BASIC",
  MN_EXT_BASIC = "MEDIGAP_PLAN_TYPE_MN_EXT_BASIC",
  MN_EXT_BASIC_NEW = "MEDIGAP_PLAN_TYPE_MN_EXT_BASIC_NEW",
  MN_HIGH_F = "MEDIGAP_PLAN_TYPE_MN_HIGH_F",
  MN_HIGH_F_NEW = "MEDIGAP_PLAN_TYPE_MN_HIGH_F_NEW",
  MN_SUPP_50 = "MEDIGAP_PLAN_TYPE_MN_SUPP_50",
  MN_SUPP_75 = "MEDIGAP_PLAN_TYPE_MN_SUPP_75",
  MN_N = "MEDIGAP_PLAN_TYPE_MN_N",
  MN_M = "MEDIGAP_PLAN_TYPE_MN_M",
  WI_BASIC = "MEDIGAP_PLAN_TYPE_WI_BASIC",
  WI_HIGH_DEDUCTIBLE = "MEDIGAP_PLAN_TYPE_WI_HIGH_DEDUCTIBLE",
  WI_25_COST = "MEDIGAP_PLAN_TYPE_WI_25_COST",
  WI_50_COST = "MEDIGAP_PLAN_TYPE_WI_50_COST",
}

enum MedigapPlanBenefit {
  SKILLED_NURSING = "medigap.benefit.skilled_nursing",
  PART_A_DEDUCTIBLE = "medigap.benefit.part_a_deductible",
  PART_B_DEDUCTIBLE = "medigap.benefit.part_b_deductible",
  PART_B_EXCESS = "medigap.benefit.part_b_excess_charges",
  FOREIGN_TRAVEL_EMERGENCY = "medigap.benefit.foreign_travel_emergency",
}

export const planBenefitMapping = {
  1: MedigapPlanBenefit.SKILLED_NURSING,
  2: MedigapPlanBenefit.PART_A_DEDUCTIBLE,
  3: MedigapPlanBenefit.PART_B_DEDUCTIBLE,
  4: MedigapPlanBenefit.PART_B_EXCESS,
  5: MedigapPlanBenefit.FOREIGN_TRAVEL_EMERGENCY,
};

export enum MedigapWaiverType {
  NONE = "NONE",
  MA = "MA",
  MN = "MN",
  WI = "WI",
}

// Plan benefits are a comma-separated string ("1, 2"). This will turn those values into an array
// and translate them.
export const getMedigapPlanBenefits = (
  plan: MedigapPlan,
  t: (str: string) => string
): string[] => {
  const { plan_benefits } = plan;
  if (!plan_benefits) {
    return [];
  }

  return plan_benefits
    .split(",")
    .map(benefit => t(planBenefitMapping[benefit.trim()]));
};

export const isWaiverState = ({
  state,
  waiver,
}: {
  state: string;
  waiver: { [key: string]: MedigapPlan[] };
}): boolean => {
  const waiverStateAbbreviations = Object.keys(waiver);

  return waiverStateAbbreviations.includes(state);
};

export const getMedigapPlansForState = ({
  state,
  pricingYear,
}: {
  state: string;
  pricingYear: string;
}): MedigapPlan[] => {
  const { waiver, nonWaiver } = getMedigapPlans(pricingYear);

  return isWaiverState({ state, waiver }) ? waiver[state] : nonWaiver;
};

export const getPhasedOutPlanWaiverStatus = (
  plan: MedigapPlan
): MedigapWaiverType | undefined => {
  const planType = plan.plan_type as MedigapPlanType;
  if (
    [MedigapPlanType.C, MedigapPlanType.F, MedigapPlanType.HIGH_F].includes(
      planType
    )
  ) {
    return MedigapWaiverType.NONE;
  }
  if (planType === MedigapPlanType.MA_SUPP_1) {
    return MedigapWaiverType.MA;
  }
  if (
    [MedigapPlanType.MN_EXT_BASIC, MedigapPlanType.MN_HIGH_F].includes(planType)
  ) {
    return MedigapWaiverType.MN;
  }
  if (planType === MedigapPlanType.WI_HIGH_DEDUCTIBLE) {
    return MedigapWaiverType.WI;
  }
  return undefined;
};

export const getMedigapPlanByType = ({
  planType,
  pricingYear,
}: {
  planType: MedigapPlanType;
  pricingYear: string;
}): MedigapPlan | undefined => {
  const { waiver, nonWaiver } = getMedigapPlans(pricingYear);

  return [...nonWaiver, ...waiver.MA, ...waiver.MN, ...waiver.WI].find(
    plan => plan.plan_type === planType
  );
};

/**
 * Interpolate a translation with the appropriate Medigap Part B Deductible value,
 * depending on flags for current plan year and displaying next year's Medigap pricing
 */
export const usePartBDeductible = (translationKey: string): string => {
  const fm = useContext(IntlContext).formatMessage;
  const { feEnableNextYearMedigapPricing, mctCurrentPlanYear } = useFlags();
  const medigapPricingYear = getMedigapPricingYear({
    feEnableNextYearMedigapPricing,
    mctCurrentPlanYear,
  });
  const part_b_deductible = String(
    CONSTANTS.partBHealthDeductible[medigapPricingYear]
  );
  return fm({ id: translationKey }, { part_b_deductible });
};
